import React, { useState } from "react";
import styled from "@emotion/styled";
import { useLoadScript, GoogleMap } from "@react-google-maps/api";
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import { Box, Text } from "@atoms";
import { Bugsnag } from "@services";
import { theme } from "@styles";
import { useStoreAppValue } from "@hooks";
import { IGooglePlace } from "../BeneficiaryType";
// == Types ================================================================

interface IProps {
  setGooglePlace: (googlePlace: IGooglePlace) => void;
  setFieldValue: (field, value) => void;
  setManualOrgEntry: (boolean) => void;
  organizationName?: string | null;
  orgPlaceholder: string;
}

// == Constants ============================================================

const googleMapsLibraries = ["places"];
BeneficiaryOrganization.defaultProps = {};

// STUB CREATION FOR WHEN A USER SELECTS LAST OPTION
const CREATE_NEW_SUGGESTION = {
  active: true,
  description: "CREATE_NEW",
  id: "create-new",
  index: 0,
};

// == Component ============================================================

export default function BeneficiaryOrganization({
  setGooglePlace,
  setFieldValue,
  setManualOrgEntry,
  organizationName,
  orgPlaceholder,
}: IProps) {
  const { currency, gmapsKey } = useStoreAppValue();
  const [address, setAddress] = useState(organizationName || "");
  const [map, setMap] = useState(null);
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: gmapsKey,
    libraries: googleMapsLibraries,
    mapTypeControl: false,
  });

  const getWebsite = (placeId) => {
    const request = {
      placeId,
      fields: ["website"],
    };
    if (map) {
      const service = new google.maps.places.PlacesService(map);
      service.getDetails(request, setWebsite);
    }
  };

  const setWebsite = (place, status) => {
    if (status == google.maps.places.PlacesServiceStatus.OK) {
      if (place?.website) {
        setFieldValue("website", place.website.replace(/(^\w+:|^)\/\//, ""));
      } else {
        setFieldValue("website", "");
      }
    }
  };

  const handleChange = (address: string) => {
    setAddress(address);
  };

  const handleSelect = (address: string, e) => {
    if (address === CREATE_NEW_SUGGESTION.description) {
      onClickManualEntry();
    } else {
      geocodeByAddress(address)
        .then((results: any) => {
          setAddress(address);
          getLatLng(results[0]);
          setGooglePlace({ placeId: results[0].place_id });
          getWebsite(results[0].place_id);
          setFieldValue("googlePlacesId", results[0].place_id);
          setFieldValue("teamName", address.substr(0, address.indexOf(",")));
          setFieldValue(
            "orgStreet",
            results[0].formatted_address.substr(0, results[0].formatted_address.indexOf(","))
          );
          results[0].address_components.map((component) => {
            component.types.map((type) => {
              if (type === "locality") setFieldValue("orgCity", component.long_name);
              if (type === "sublocality") setFieldValue("orgCity", component.long_name);
              if (type === "administrative_area_level_1")
                setFieldValue("orgProvince", component.short_name);
              if (type === "postal_code") setFieldValue("orgPostalCode", component.long_name);
            });
          });
        })
        .catch(Bugsnag.notify);
    }
  };

  const onClickManualEntry = () => {
    setManualOrgEntry(true);
    setFieldValue("googlePlacesId", null);
    setFieldValue("teamName", "");
    setFieldValue("orgStreet", null);
    setFieldValue("orgCity", null);
    setFieldValue("orgProvince", null);
    setFieldValue("orgPostalCode", null);
    setFieldValue("website", "");
  };

  const onMapLoad = (map) => {
    setMap(map);
  };

  if (!isLoaded) return null;

  return (
    <>
      <GoogleMap id="data-example" onLoad={onMapLoad} />
      <PlacesAutocomplete
        searchOptions={{
          location: process.browser ? new window.google.maps.LatLng(43.710075, -79.391299) : null,
          radius: 3000,
          strictBounds: true,
          types: ["establishment"],
          componentRestrictions: { country: currency === "USD" ? ["us"] : ["ca"] },
        }}
        value={address}
        onChange={handleChange}
        onSelect={handleSelect}
      >
        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
          <EPlacesContainer position="relative" width={[1]}>
            <ELabel className="active">Organization</ELabel>
            <EInput
              aria-label={orgPlaceholder}
              autocomplete="off"
              {...getInputProps({
                placeholder: orgPlaceholder,
                className: "location-search-input form-control input-field",
              })}
              onKeyDown={(e) => {
                if (e.keyCode === 13) {
                  Materialize?.toast?.(
                    "Please select from the dropdown.",
                    4000,
                    "rounded toast-error"
                  );
                  e.preventDefault();
                }
              }}
            />

            <EPlacesAutoComplete border={suggestions.length > 0 ? "silver" : "none"}>
              {loading && <ELoadingView bg="white">Loading...</ELoadingView>}
              {suggestions.map((suggestion) => {
                const className = suggestion.active ? "suggestion-item--active" : "suggestion-item";
                const style = suggestion.active
                  ? {
                      backgroundColor: `${theme.colors.lightGreyBg}`,
                      cursor: "pointer",
                    }
                  : { backgroundColor: `${theme.colors.white}`, cursor: "pointer" };
                return (
                  <Box
                    py={1}
                    {...getSuggestionItemProps(suggestion, {
                      className,
                      style,
                    })}
                    key={suggestion.placeId}
                  >
                    <Box pl={2}>{suggestion.description}</Box>
                  </Box>
                );
              })}
              {suggestions.length > 0 && (
                <ECreateSuggestionBox
                  py={1}
                  {...getSuggestionItemProps(
                    { ...CREATE_NEW_SUGGESTION, index: suggestions.length },
                    {
                      className: "suggestion-item",
                      style: {
                        backgroundColor: `${theme.colors.white}`,
                        cursor: "pointer",
                      },
                    }
                  )}
                >
                  <Box pl={2}>
                    <Text color="primary">
                      My organization is not listed above. Click here to add.
                    </Text>
                  </Box>
                </ECreateSuggestionBox>
              )}
            </EPlacesAutoComplete>

            {/* <EBox mt={1}>
              <Text fontSize={0}>Can't find your organization? </Text>
              <Link fontSize={0} onClick={onClickManualEntry}>
                Click here to enter manually
              </Link>
            </EBox> */}
          </EPlacesContainer>
        )}
      </PlacesAutocomplete>
    </>
  );
}

// == Styles ===============================================================

const EPlacesContainer = styled(Box)`
  position: relative;
  margin-bottom: 2.5rem;
`;

const EPlacesAutoComplete = styled(Box)`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1;
`;

const ELoadingView = styled(Box)`
  cursor: pointer;
  background: ${({ theme }) => theme.colors.white} !important;

  &:hover {
    background: ${({ theme }) => theme.colors.gray} !important;
  }
`;

const EInput = styled.input`
  margin-bottom: 0 !important;
  height: 40px !important;
  padding-top: 0 !important;
  width: 50%;
  padding-left: 10px !important;
`;

const ELabel = styled.label`
  margin-bottom: 0 !important;
`;

// const EBox = styled(Box)`
//   position: absolute;
//   top: 100%;
//   left: 0;
//   right: 0;
// `;

const ECreateSuggestionBox = styled(Box)`
  &:hover {
    background: ${({ theme }) => theme.colors.lightGreyBg} !important;
  }
`;
